/**
 * Live GPS Smoothing Pipeline — Uber-like rendering
 * 
 * Pipeline: GPS brut → Filter → Interpolate → Catmull-Rom Smooth → Buffer
 * Zero Roads API dependency.
 */

// ─── Configurable Constants ──────────────────────────
export const SMOOTHING_CONFIG = {
  INTERPOLATION_DISTANCE_THRESHOLD: 15, // meters: add intermediate points if gap > this
  MAX_POINTS_BUFFER: 50,                // sliding window of points
  ANIMATION_DURATION: 1000,             // ms for position transition
  MIN_DISTANCE_FILTER: 5,              // meters: ignore points closer than this
  MAX_ACCURACY: 20,                    // meters: ignore points with worse accuracy
  MAX_JUMP_DISTANCE: 200,             // meters: reject teleport-like jumps
  MIN_SPEED_FILTER: 0,                // km/h: 0 = keep stationary points
  CATMULL_ROM_TENSION: 0.5,           // 0 = sharp, 1 = very smooth
  CATMULL_ROM_SEGMENTS: 8,            // subdivisions per segment
  HEADING_BLEND_FACTOR: 0.4,          // 0 = instant rotation, 1 = very slow
};

// ─── Haversine Distance (meters) ─────────────────────
export function haversineM(a, b) {
  const R = 6371000;
  const toRad = d => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

// ─── Bearing (degrees) ──────────────────────────────
export function bearing(a, b) {
  const toRad = d => (d * Math.PI) / 180;
  const toDeg = r => (r * 180) / Math.PI;
  const dLng = toRad(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(toRad(b.lat));
  const x = Math.cos(toRad(a.lat)) * Math.sin(toRad(b.lat)) -
    Math.sin(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

// ─── Smooth Heading Interpolation ────────────────────
export function smoothHeading(currentH, targetH, blend = SMOOTHING_CONFIG.HEADING_BLEND_FACTOR) {
  let diff = targetH - currentH;
  if (diff > 180) diff -= 360;
  if (diff < -180) diff += 360;
  return (currentH + diff * blend + 360) % 360;
}

// ─── 1. GPS Point Filter ─────────────────────────────
export function filterGpsPoint(point, lastAccepted, config = SMOOTHING_CONFIG) {
  // Reject bad accuracy
  if (point.accuracy && point.accuracy > config.MAX_ACCURACY) return false;

  if (!lastAccepted) return true;

  const dist = haversineM(lastAccepted, point);

  // Reject too close
  if (dist < config.MIN_DISTANCE_FILTER) return false;

  // Reject teleport jumps
  if (dist > config.MAX_JUMP_DISTANCE) return false;

  return true;
}

// ─── 2. Interpolate Between Distant Points ───────────
export function interpolatePoints(a, b, config = SMOOTHING_CONFIG) {
  const dist = haversineM(a, b);
  if (dist <= config.INTERPOLATION_DISTANCE_THRESHOLD) return [a, b];

  const n = Math.ceil(dist / config.INTERPOLATION_DISTANCE_THRESHOLD);
  const result = [a];
  for (let i = 1; i < n; i++) {
    const t = i / n;
    result.push({
      lat: a.lat + (b.lat - a.lat) * t,
      lng: a.lng + (b.lng - a.lng) * t,
      interpolated: true,
    });
  }
  result.push(b);
  return result;
}

// ─── 3. Catmull-Rom Spline Smoothing ─────────────────
function catmullRomPoint(p0, p1, p2, p3, t, tension) {
  const t2 = t * t;
  const t3 = t2 * t;
  const s = (1 - tension) / 2;

  // Correct cardinal spline basis — s only applies to derivative terms,
  // NOT to the identity terms. This ensures P(0)=P1 and P(1)=P2 for any tension.
  const h1 = -s * t3 + 2 * s * t2 - s * t;
  const h2 = (2 - s) * t3 + (s - 3) * t2 + 1;
  const h3 = (s - 2) * t3 + (3 - 2 * s) * t2 + s * t;
  const h4 = s * t3 - s * t2;

  const lat = h1 * p0.lat + h2 * p1.lat + h3 * p2.lat + h4 * p3.lat;
  const lng = h1 * p0.lng + h2 * p1.lng + h3 * p2.lng + h4 * p3.lng;

  return { lat, lng };
}

export function smoothWithCatmullRom(points, config = SMOOTHING_CONFIG) {
  if (points.length < 3) return points;

  const result = [];
  const tension = config.CATMULL_ROM_TENSION;
  const segments = config.CATMULL_ROM_SEGMENTS;

  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];

    // Skip spline on large gaps — just connect straight
    const gapM = haversineM(p1, p2);
    if (gapM > 500) {
      result.push(p1);
      continue;
    }

    const p0 = points[Math.max(0, i - 1)];
    const p3 = points[Math.min(points.length - 1, i + 2)];

    result.push(p1);
    for (let j = 1; j < segments; j++) {
      const t = j / segments;
      result.push(catmullRomPoint(p0, p1, p2, p3, t, tension));
    }
  }
  result.push(points[points.length - 1]);

  return result;
}

// ─── 4. Sliding Buffer Manager ───────────────────────
export class GpsBuffer {
  constructor(maxSize = SMOOTHING_CONFIG.MAX_POINTS_BUFFER) {
    this.maxSize = maxSize;
    this.raw = [];       // Raw accepted points
    this.smoothed = [];  // After Catmull-Rom
  }

  addPoint(point) {
    const last = this.raw[this.raw.length - 1];
    if (!filterGpsPoint(point, last)) return false;

    this.raw.push(point);

    // Trim to max size
    if (this.raw.length > this.maxSize) {
      this.raw = this.raw.slice(-this.maxSize);
    }

    // Re-smooth only the tail (incremental)
    this._updateSmoothed();
    return true;
  }

  _updateSmoothed() {
    if (this.raw.length < 2) {
      this.smoothed = [...this.raw];
      return;
    }

    // Interpolate between distant points
    const interpolated = [];
    for (let i = 0; i < this.raw.length - 1; i++) {
      const pts = interpolatePoints(this.raw[i], this.raw[i + 1]);
      interpolated.push(...pts.slice(0, -1));
    }
    interpolated.push(this.raw[this.raw.length - 1]);

    // Apply Catmull-Rom
    this.smoothed = smoothWithCatmullRom(interpolated);
  }

  getSmoothedPath() {
    return this.smoothed;
  }

  getLastRawPoint() {
    return this.raw[this.raw.length - 1] || null;
  }

  getHeading() {
    if (this.raw.length < 2) return 0;
    const a = this.raw[this.raw.length - 2];
    const b = this.raw[this.raw.length - 1];
    return bearing(a, b);
  }

  clear() {
    this.raw = [];
    this.smoothed = [];
  }

  get length() {
    return this.raw.length;
  }
}
